import React, { useState } from 'react';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Urls } from 'data';
import { useAPI } from 'hooks';
import Locales from 'locales';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { AuthCard, AuthPageLayout } from 'components/shared/AuthPageLayout';

const ResetPassword = () => {
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [displayPwd, setDisplayPwd] = useState(false);
  const [displayConfirmPwd, setDisplayConfirmPwd] = useState(false);
  const [pwdRuleSize, setPwdRuleSize] = useState(false);
  const [pwdRuleUppercase, setPwdRuleUppercase] = useState(false);
  const [pwdRuleLowercase, setPwdRuleLowercase] = useState(false);
  const [pwdRuleNumber, setPwdRuleNumber] = useState(false);
  const [pwdRuleSpecial, setPwdRuleSpecial] = useState(false);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;
  const navigate = useNavigate();

  const { api } = useAPI();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const { mutate: resetPassword } = useMutation({
    mutationFn: () => api.post('auth/user_token/reset_password/', { token, password: pwd, password2: confirmPwd }),
    onSuccess: () => {
      toast.success(Locales.auth.password_reset_with_success);
      navigate(Urls.front.login);
    },
  });

  const isValidPwd = pwdRuleSize && pwdRuleUppercase && pwdRuleLowercase && pwdRuleNumber && pwdRuleSpecial;

  const toggleDisplayPassword = () => setDisplayPwd((val) => !val);
  const toggleDisplayConfirmPassword = () => setDisplayConfirmPwd((val) => !val);

  const validatePassword = (password) => {
    setPwdRuleSize(password.length > 8);
    setPwdRuleUppercase(/[A-Z]/.test(password));
    setPwdRuleLowercase(/[a-z]/.test(password));
    setPwdRuleNumber(/\d/.test(password));
    setPwdRuleSpecial(/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password));
  };

  const handlePasswordChange = (value) => {
    const newPassword = value;
    setPwd(newPassword);
    validatePassword(newPassword);
  };

  const enableSubmitButton = isValidPwd && pwd === confirmPwd;

  return (
    <AuthPageLayout>
      <AuthCard className="max-w-[480px]">
        <div className="space-y-6">
          <div className="mb-[25px] text-left">
            <h1 className="text-2xl font-semibold leading-[1.3] tracking-[-0.55px] text-[#21312a] max-[520px]:text-[22px]">
              {Locales.auth.reset_password_title}
            </h1>
            <p className="mt-2 text-sm leading-[1.6] text-[#62736a]">{Locales.auth.reset_password_message}</p>
          </div>

          <form className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                {Locales.auth.email}
              </Label>
              <Input id="email" type="email" value={email || ''} disabled variant="disabled" size="sm" />
            </div>

            <div className="space-y-3">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                {Locales.auth.password}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={displayPwd ? 'text' : 'password'}
                  placeholder="••••••••••••••••••"
                  value={pwd}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  variant="hajj"
                  size="sm"
                />
                <button
                  type="button"
                  onClick={toggleDisplayPassword}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {displayPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                {Locales.auth.confirm_password}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={displayConfirmPwd ? 'text' : 'password'}
                  placeholder="••••••••••••••••••"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  variant="hajj"
                  size="sm"
                />
                <button
                  type="button"
                  onClick={toggleDisplayConfirmPassword}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {displayConfirmPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <ul className="flex flex-wrap gap-2 pt-4" aria-label="Exigences du mot de passe">
              {[
                [Locales.auth.password_rule_size, pwdRuleSize],
                [Locales.auth.password_rule_uppercase, pwdRuleUppercase],
                [Locales.auth.password_rule_lowercase, pwdRuleLowercase],
                [Locales.auth.password_rule_number, pwdRuleNumber],
                [Locales.auth.password_rule_special, pwdRuleSpecial],
              ].map(([requirement, isMet]) => (
                <li key={requirement}>
                  <Badge variant={isMet ? 'hajj' : 'outline'}>{requirement}</Badge>
                </li>
              ))}
            </ul>

            <Button
              type="button"
              onClick={resetPassword}
              variant="hajj"
              className="w-full"
              size="sm"
              disabled={!enableSubmitButton || isMutating}
            >
              {isMutating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Création en cours...
                </>
              ) : (
                Locales.auth.create_account
              )}
            </Button>
          </form>
        </div>
      </AuthCard>
    </AuthPageLayout>
  );
};

export default ResetPassword;
